import { useState } from "react";
import { Loader2, Plus, Trash2, X, Check, ThumbsUp, CheckSquare, Columns3, Frame } from "lucide-react";
import {
  useBoard,
  useBoardActions,
  type PizarraCard,
  type PizarraColumn,
} from "../api/projects";
import { LienzoCanvas } from "./LienzoCanvas";

const COLORS = ["#fde68a", "#bbf7d0", "#bfdbfe", "#fbcfe8", "#ddd6fe", "#fecaca", "#e2e8f0"];

export function PizarraTab({ projectId }: { projectId: number }) {
  const [mode, setMode] = useState<"tablero" | "lienzo">("tablero");

  return (
    <div className="flex flex-col gap-3">
      <div className="inline-flex self-start rounded-lg border border-slate-300 overflow-hidden">
        <button onClick={() => setMode("tablero")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold ${mode === "tablero" ? "bg-brand-700 text-white" : "bg-white text-slate-600"}`}>
          <Columns3 size={15} /> Tablero
        </button>
        <button onClick={() => setMode("lienzo")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold ${mode === "lienzo" ? "bg-brand-700 text-white" : "bg-white text-slate-600"}`}>
          <Frame size={15} /> Lienzo
        </button>
      </div>
      {mode === "tablero" ? <TableroPizarra projectId={projectId} /> : <LienzoCanvas projectId={projectId} />}
    </div>
  );
}

function TableroPizarra({ projectId }: { projectId: number }) {
  const { data: columns, isLoading } = useBoard(projectId);
  const actions = useBoardActions(projectId);
  const [nuevaCol, setNuevaCol] = useState("");
  const [dragCard, setDragCard] = useState<PizarraCard | null>(null);

  // Mueve una tarjeta a una columna, opcionalmente antes de otra tarjeta.
  function moveCard(cardId: number, targetColId: number, beforeCardId: number | null) {
    if (!columns) return;
    const colsMap = columns.map((c) => ({
      id: c.id,
      cards: c.tarjetas.filter((t) => t.id !== cardId).map((t) => t.id),
    }));
    const target = colsMap.find((c) => c.id === targetColId);
    if (!target) return;
    let idx = beforeCardId ? target.cards.indexOf(beforeCardId) : target.cards.length;
    if (idx < 0) idx = target.cards.length;
    target.cards.splice(idx, 0, cardId);

    const payload: { id: number; columna_id: number; orden: number }[] = [];
    colsMap.forEach((c) => c.cards.forEach((id, i) => payload.push({ id, columna_id: c.id, orden: i + 1 })));
    actions.reorder.mutate(payload);
    setDragCard(null);
  }

  if (isLoading) {
    return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando pizarra…</div>;
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-4 items-start">
      {(columns ?? []).map((col) => (
        <Column
          key={col.id}
          column={col}
          actions={actions}
          dragCard={dragCard}
          setDragCard={setDragCard}
          moveCard={moveCard}
        />
      ))}

      <form
        onSubmit={(e) => { e.preventDefault(); if (nuevaCol.trim()) { actions.addColumn.mutate(nuevaCol.trim()); setNuevaCol(""); } }}
        className="shrink-0 w-64 rounded-2xl border-2 border-dashed border-slate-300 p-3"
      >
        <input className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="+ Nueva columna" value={nuevaCol} onChange={(e) => setNuevaCol(e.target.value)} />
        {nuevaCol.trim() && <button type="submit" className="mt-2 w-full rounded-lg bg-brand-700 hover:bg-brand-800 text-white py-1.5 text-sm font-bold">Añadir columna</button>}
      </form>
    </div>
  );
}

function Column({
  column, actions, dragCard, setDragCard, moveCard,
}: {
  column: PizarraColumn;
  actions: ReturnType<typeof useBoardActions>;
  dragCard: PizarraCard | null;
  setDragCard: (c: PizarraCard | null) => void;
  moveCard: (cardId: number, targetColId: number, beforeCardId: number | null) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [texto, setTexto] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [editTitle, setEditTitle] = useState(false);
  const [titulo, setTitulo] = useState(column.titulo);

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => { if (dragCard) moveCard(dragCard.id, column.id, null); }}
      className="shrink-0 w-64 rounded-2xl bg-slate-100 border border-slate-200 p-3 flex flex-col gap-2"
    >
      <div className="flex items-center gap-2">
        {editTitle ? (
          <input
            className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm font-bold"
            value={titulo} autoFocus
            onChange={(e) => setTitulo(e.target.value)}
            onBlur={() => { setEditTitle(false); if (titulo.trim() && titulo !== column.titulo) actions.renameColumn.mutate({ id: column.id, titulo: titulo.trim() }); }}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
          />
        ) : (
          <button onClick={() => setEditTitle(true)} className="flex-1 text-left font-black text-slate-700 text-sm truncate">{column.titulo}</button>
        )}
        <span className="text-xs font-bold text-slate-400">{column.tarjetas.length}</span>
        <button onClick={() => { if (confirm("¿Eliminar columna y sus tarjetas?")) actions.deleteColumn.mutate(column.id); }} className="text-slate-300 hover:text-rose-600"><Trash2 size={14} /></button>
      </div>

      <div className="flex flex-col gap-2 min-h-[8px]">
        {column.tarjetas.map((card) => (
          <Card
            key={card.id}
            card={card}
            actions={actions}
            onDragStart={() => setDragCard(card)}
            onDropBefore={() => { if (dragCard) moveCard(dragCard.id, card.columna_id, card.id); }}
          />
        ))}
      </div>

      {adding ? (
        <form
          onSubmit={(e) => { e.preventDefault(); if (texto.trim()) { actions.addCard.mutate({ columnId: column.id, texto: texto.trim(), color }); setTexto(""); setAdding(false); } }}
          className="rounded-xl bg-white border border-slate-200 p-2 flex flex-col gap-2"
        >
          <textarea className="w-full rounded border border-slate-200 px-2 py-1 text-sm min-h-[54px]" placeholder="Escribe una idea…" value={texto} autoFocus onChange={(e) => setTexto(e.target.value)} />
          <div className="flex items-center gap-1">
            {COLORS.map((c) => <button key={c} type="button" onClick={() => setColor(c)} className={`w-5 h-5 rounded-full border-2 ${color === c ? "border-slate-800" : "border-transparent"}`} style={{ background: c }} />)}
          </div>
          <div className="flex gap-1">
            <button type="submit" className="flex-1 rounded-lg bg-brand-700 hover:bg-brand-800 text-white py-1 text-xs font-bold">Añadir</button>
            <button type="button" onClick={() => { setAdding(false); setTexto(""); }} className="rounded-lg border border-slate-300 px-2 text-slate-500"><X size={14} /></button>
          </div>
        </form>
      ) : (
        <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-bold text-slate-500 hover:bg-slate-200"><Plus size={15} /> Añadir tarjeta</button>
      )}
    </div>
  );
}

function Card({
  card, actions, onDragStart, onDropBefore,
}: {
  card: PizarraCard;
  actions: ReturnType<typeof useBoardActions>;
  onDragStart: () => void;
  onDropBefore: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [texto, setTexto] = useState(card.texto);

  return (
    <div
      draggable={!editing}
      onDragStart={onDragStart}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onDrop={(e) => { e.stopPropagation(); onDropBefore(); }}
      className="group rounded-xl p-2.5 shadow-sm text-sm text-slate-800 cursor-grab active:cursor-grabbing"
      style={{ background: card.color }}
    >
      {editing ? (
        <div className="flex flex-col gap-2">
          <textarea className="w-full rounded border border-slate-300 px-2 py-1 text-sm min-h-[54px] bg-white/80" value={texto} autoFocus onChange={(e) => setTexto(e.target.value)} />
          <div className="flex items-center gap-1">
            {COLORS.map((c) => <button key={c} type="button" onClick={() => actions.updateCard.mutate({ id: card.id, payload: { color: c } })} className={`w-4 h-4 rounded-full border ${card.color === c ? "border-slate-800" : "border-transparent"}`} style={{ background: c }} />)}
            <button onClick={() => { actions.updateCard.mutate({ id: card.id, payload: { texto: texto.trim() || card.texto } }); setEditing(false); }} className="ml-auto text-emerald-700"><Check size={16} /></button>
            <button onClick={() => setEditing(false)} className="text-slate-500"><X size={16} /></button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-1">
            <span className="flex-1 whitespace-pre-wrap break-words" onDoubleClick={() => setEditing(true)}>{card.texto}</span>
            <div className="opacity-0 group-hover:opacity-100 flex flex-col gap-1">
              <button onClick={() => setEditing(true)} className="text-slate-500 hover:text-slate-800 text-xs" title="Editar">✎</button>
              <button
                onClick={() => { if (confirm("¿Convertir esta idea en una tarea del proyecto?")) actions.toTask.mutate(card.id); }}
                className="text-slate-500 hover:text-brand-700"
                title="Convertir en tarea"
              >
                <CheckSquare size={13} />
              </button>
              <button onClick={() => actions.deleteCard.mutate(card.id)} className="text-rose-400 hover:text-rose-600" title="Eliminar"><Trash2 size={13} /></button>
            </div>
          </div>
          <button
            onClick={() => actions.vote.mutate(card.id)}
            className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold transition ${
              card.votado ? "bg-brand-700 text-white" : "bg-white/70 text-slate-600 hover:bg-white"
            }`}
            title="Votar idea"
          >
            <ThumbsUp size={12} /> {card.votos ?? 0}
          </button>
        </>
      )}
    </div>
  );
}
